<template>
  <div
    class="button-row-container"
    :class="{ 'review-navigation': showPrevious || showNext || submitStyle }"
  >
    <div class="button-wrapper">
      <key-capture
        @keydown="handleKeyDown"
      ></key-capture>
      <!-- Optional content on the left of the footer, e.g. question feedback -->
      <div
        v-if="$slots.start"
        class="start-slot"
      >
        <slot name="start" />
      </div>
      <div
        v-if="showFlashcardStatuses"
        class="tag-wrapper"
      >
        <button
          v-for="status in answeredFlashcardStatuses"
          :key="status.id"
          class="button-choice"
          :data-test="`flashcard-status-${status.name}`"
          @click="tag(status.id)"
        >
          <icon
            name="tag-uui"
            :variant="status.name"
          ></icon>
          <span>{{ status.displayName }}</span>
        </button>
        <pop-over
          v-if="showChooseTagsPopover"
          use-circle
          icon-name="tag-uui-white"
        >
          <template #header>
            <span>Choose a tag to continue</span>
          </template>
          Tags can be used to sort and review questions later.
        </pop-over>
      </div>
      <template v-else>
        <navigation-buttons
          v-if="showPrevious || showNext"
          :disable-next="disableNext"
          :disable-previous="disablePrevious"
          :show-next="showNext"
          :show-previous="showPrevious"
          @next-flashcard="$emit('next-flashcard')"
          @previous-flashcard="$emit('previous-flashcard')"
        ></navigation-buttons>
        <button
          v-if="previousButtonText"
          class="button-choice"
          data-test="previous-question-button"
          @click="$emit('previous')"
        >
          {{ previousButtonText }}
        </button>
        <button
          v-if="!hidePrimary || reservePrimarySpace"
          :aria-hidden="primaryReserved ? 'true' : null"
          :class="[
            submitStyle || reservePrimarySpace ? 'submit-button' : 'button-choice',
            { 'is-spacer': primaryReserved },
          ]"
          :data-test="primaryReserved ? 'done-button-spacer' : 'next-question-button'"
          :tabindex="primaryReserved ? -1 : null"
          @click="onPrimaryClick"
        >
          {{ buttonText }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/singletons/icon';
import KeyCapture from '@/components/singletons/KeyCapture';
import PopOver from '@/components/singletons/PopOver';
import NavigationButtons from './NavigationButtons.vue';

export default {
  components: {
    Icon,
    KeyCapture,
    NavigationButtons,
    PopOver,
  },
  props: {
    buttonText: {
      type: String,
      default: 'Next Question',
    },
    showFlashcardStatuses: {
      type: Boolean,
      default: false,
    },
    // Shows a previous button next to the next button when set
    previousButtonText: {
      type: String,
      default: null,
    },
    showPrevious: {
      type: Boolean,
      default: false,
    },
    showNext: {
      type: Boolean,
      default: false,
    },
    disablePrevious: {
      type: Boolean,
      default: false,
    },
    disableNext: {
      type: Boolean,
      default: false,
    },
    hidePrimary: {
      type: Boolean,
      default: false,
    },
    submitStyle: {
      type: Boolean,
      default: false,
    },
    reservePrimarySpace: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['previous', 'tag', 'previous-flashcard', 'next-flashcard'],
  computed: {
    answeredFlashcardStatuses() {
      return [...this.$store.getters['flashcardStatuses/answeredFlashcardStatuses']]
        .sort((a, b) => a.order - b.order);
    },
    greenFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/greenFlashcardStatus'];
    },
    miraDrawerShown() {
      return this.$store.getters['ui/miraDrawerShown'];
    },
    showChooseTagsPopover() {
      return this.$store.getters['flashcardStates/flashcardStateList'].length === 0;
    },
    primaryReserved() {
      return this.hidePrimary && this.reservePrimarySpace;
    },
  },
  methods: {
    onPrimaryClick() {
      if (this.primaryReserved) return;
      this.tag(this.greenFlashcardStatus.id);
    },
    tag(flashcardStatusId) {
      this.$emit('tag', flashcardStatusId);
    },
    handleKeyDown(key) {
      if (this.miraDrawerShown || this.hidePrimary) return;
      if (key.length != 1) return;

      const matchingStatus = this.answeredFlashcardStatuses.find(
        status => status.displayName.toUpperCase()[0] === key.toUpperCase(),
      );
      if (matchingStatus) {
        this.tag(matchingStatus.id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.button-row-container {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: rem-calc(8);
  justify-content: center;
  padding: 1rem;
  background-color: #F9FAFB;
  z-index: 10;

  .button-wrapper {
    grid-column: 1 / 13;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: rem-calc(8);
    flex-grow: 1;
  }
  .start-slot {
    margin-right: auto;
  }

  .tag-wrapper {
    position: relative;
    display: flex;
    gap: rem-calc(8);
  }

  .submit-button {
    background-color: #155eef;
    color: white;
    height: rem-calc(48);
    font-weight: 600;
    font-size: rem-calc(18);
    padding-right: rem-calc(38);
    padding-left: rem-calc(38);
    border-radius: rem-calc(8);
    flex-shrink: 0;

    &.is-spacer {
      visibility: hidden;
    }
  }

  .button-choice {
    background-color: white;
    border: 1px solid #e3e8ef;
    border-radius: rem-calc(6);
    color: #232323;
    // flex box properties
    display: flex;
    align-items: center;
    justify-content: center;
    gap: rem-calc(6);
    // text properties
    font-size: rem-calc(12);
    font-weight: 600;
    line-height: rem-calc(16);
    padding: rem-calc(8) rem-calc(8);
    white-space: nowrap;
    flex-grow: 1;
  }
}

@include breakpoint(tablet) {
  .button-row-container {
    left: rem-calc(88);
    bottom: 0;

    .button-wrapper {
      justify-content: center;
    }

    .button-choice {
      flex-grow: 0;
      justify-content: center;
      font-size: rem-calc(16);
      line-height: rem-calc(24);
      padding: rem-calc(8) rem-calc(16);
    }
  }
}

@include breakpoint(desktop) {
  .button-row-container {
    left: rem-calc(88);
    bottom: 0;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: rem-calc(32);
    margin-right: 2rem;
    margin-left: 2rem;
    margin-bottom: 2rem;
    padding: 0;


    .button-wrapper {
      grid-column: 5 / 13;
    }

    .button-choice {
      flex-grow: 0;
      justify-content: center;
      font-size: rem-calc(16);
      line-height: rem-calc(24);
      padding: rem-calc(8) rem-calc(16);
    }
  }
}

.button-row-container.review-navigation {
  .button-wrapper {
    justify-content: flex-end;
    gap: rem-calc(16);
  }

  :deep(.navigation) {
    font-size: rem-calc(16);
    font-weight: 600;
    line-height: rem-calc(24);
    gap: rem-calc(6);
  }

  :deep(.navigation img.icon-arrow-left-uui) {
    width: rem-calc(16);
    height: rem-calc(16);
  }

  :deep(.navigation img.icon-arrow-right-uui) {
    width: rem-calc(23);
    height: rem-calc(23);
    margin-left: rem-calc(-4);
  }
}
</style>
